import React, { useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { DataGrid, GridFooterContainer, GridPagination } from '@mui/x-data-grid';
import debounce from 'lodash.debounce';

const ProjectLogsTable = ({ logs, budget, projectName, projectDescription }) => {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 8 });
  const handlePaginationModelChange = useCallback(
    debounce((model) => setPaginationModel(model), 300),
    []
  );

  const columns = [
    {
      field: 'startTime',
      headerName: 'Start Time',
      flex: 2,
      valueGetter: (params) => {
        if (Number(params.row.isDurationOnly) === 1) {
          return '—';
        }
        const value = params.row.startTime;
        if (!value) return '';
        return new Date(value).toLocaleString();
      },
    },
    {
      field: 'endTime',
      headerName: 'End Time',
      flex: 2,
      valueGetter: (params) => {
        if (Number(params.row.isDurationOnly) === 1) {
          return '—';
        }
        const value = params.row.endTime;
        if (!value) return '';
        return new Date(value).toLocaleString();
      },
    },
    {
      field: 'duration',
      headerName: 'Duration',
      flex: 1,
      valueGetter: (params) => {
        const dur = Number(params.row.duration) || 0;
        const totalSeconds = Math.round(dur * 3600);
        const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
        const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
        const seconds = String(totalSeconds % 60).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
      },
    },
    { field: 'activity', headerName: 'Activity', flex: 3 },
  ];

  const processedLogs = logs.map((log, index) => ({
    ...log,
    id: log.id || index,
  }));

  const calculateTotalDuration = (logs) => {
    const totalSeconds = logs.reduce((sum, log) => sum + Math.round(((Number(log.duration) || 0) * 3600)), 0);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const calculateTotalHoursNumber = (logs) => logs.reduce((sum, log) => sum + (Number(log.duration) || 0), 0);

  const titleSuffixParts = [];
  if (projectName) {
    titleSuffixParts.push(projectName);
  }
  if (projectDescription) {
    titleSuffixParts.push(projectDescription);
  }
  const titleSuffix = titleSuffixParts.length ? `: ${titleSuffixParts.join(' — ')}` : '';

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Project Logs{titleSuffix}
      </Typography>
      <Box sx={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={processedLogs}
          columns={columns}
          density="compact"
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSize={paginationModel.pageSize}
          pageSizeOptions={[5, 10, 20]}
          components={{
            Footer: () => {
              const totalHrsNum = calculateTotalHoursNumber(logs);
              const bal = budget != null ? (Number(budget) - totalHrsNum) : null;
              return (
                <GridFooterContainer>
                  <Box sx={{ padding: 1, textAlign: 'right', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ flex: 1, textAlign: 'left', paddingLeft: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <strong>Total Hours: {calculateTotalDuration(logs)}</strong>
                      {budget != null && (
                        <span>Budget: {Number(budget).toFixed(2)}</span>
                      )}
                      {budget != null && (
                        <span style={{ color: bal < 0 ? 'red' : 'inherit' }}>
                          Balance: {bal.toFixed(2)}
                        </span>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, textAlign: 'right' }}>
                      <GridPagination />
                    </Box>
                  </Box>
                </GridFooterContainer>
              );
            },
          }}
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f5f5f5',
              fontWeight: 'bold',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default ProjectLogsTable;
